import React, { createContext, useContext } from 'react'

import { Channel } from 'public/js/siteUtilities'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'

export type DisplayPageContextType = {
  channel: Channel
  channelGuid?: string | undefined
  breadcrumbBasePath: string | undefined
  breadcrumbBaseName: string
}

const defaultContext: DisplayPageContextType = {
  channel: Channel.ECOMM,
  breadcrumbBasePath: '/store',
  breadcrumbBaseName: 'Store'
}

export const DisplayPageContext =
  createContext<DisplayPageContextType>(defaultContext)

type DisplayPageContextProviderProps = {
  channel: Channel
  children: React.ReactNode
}

export const DisplayPageContextProvider = ({
  channel,
  children
}: DisplayPageContextProviderProps) => {
  const { selectedLocation } = useRestaurant()
  const { ecommPath } = useRestaurantRoutes()
  const { restaurant: siteRestaurant } = useRestaurant()

  // Pull the ecomm store nav item name from the site content to keep the breadcrumbs consistent with the Nav
  const storeHome =
    siteRestaurant?.content?.nonPrimaryCtas
      ?.find((cta) => cta.link === ecommPath)
      ?.text?.trim() || 'Store'

  const channelGuid =
    channel === Channel.ECOMM
      ? selectedLocation?.ecommMenuChannelGuid || undefined
      : // We don't need to set the view-only menu channel GUID because it's unnecessary
        undefined

  return (
    <DisplayPageContext.Provider
      value={{
        channel,
        channelGuid,
        breadcrumbBasePath: channel === Channel.ECOMM ? ecommPath : '/',
        breadcrumbBaseName: channel === Channel.ECOMM ? storeHome : 'Home'
      }}
    >
      {children}
    </DisplayPageContext.Provider>
  )
}

export const useDisplayPageContext = () => {
  const context = useContext(DisplayPageContext)
  if (!context) {
    return defaultContext
  }
  return context
}
